import classNames from 'classnames'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import {nodeReplace} from 'helpers/stringUtils'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo} from 'react'
import {Link} from 'react-router-dom'
import {NullAsDefault, renderIf} from 'views/Shared'
import {CodiContentTypes, getLinkToDetailRow} from './codiHelpers'
import CodiContext from './CodiContext'
import {GiIronCross} from 'react-icons/gi'
import {getStylesByKeys} from '../../../helpers/getStylesByKeys'
import TooltipApp from 'components/TooltipApp'

// --- configs (visual tokens) ---
export const configs = {
    root: {
        prefix: '.',
        className: 'text-center text-size-13 text-color-000 font-bold',
        content: {
            fontSize: {sm: 'text-[1rem]', md: 'text-[1.15rem]'},
            lineHeight: {sm: 1.9, md: 1.75},
            spacing: 'space-y-6',
            align: 'center',
        },
    },
    superPart: {
        prefix: '.',
        className: 'text-size-12 font-bold',
        content: {
            fontSize: {sm: 'text-[1rem]', md: 'text-[1.1rem]'},
            lineHeight: {sm: 1.9, md: 1.7},
            spacing: 'space-y-5',
            align: 'center',
        },
    },
    part: {
        prefix: '.',
        className: 'text-size-11 font-bold',
        content: {
            fontSize: {sm: 'text-[0.95rem]', md: 'text-[1rem]'},
            lineHeight: {sm: 1.9, md: 1.7},
            spacing: 'space-y-5',
            align: 'center',
        },
    },
    appendix: {
        prefix: '.',
        className: 'text-size-11 font-bold',
        content: {
            fontSize: {sm: 'text-[0.95rem]', md: 'text-[1rem]'},
            lineHeight: {sm: 1.9, md: 1.7},
            spacing: 'space-y-5',
            align: 'center',
        },
    },
    superChapter: {
        prefix: '.',
        className: 'text-size-9 text-primary font-bold',
        content: {
            fontSize: {sm: 'text-[0.95rem]', md: 'text-[1rem]'},
            lineHeight: {sm: 1.85, md: 1.65},
            spacing: 'space-y-4',
            align: 'center',
        },
    },
    superSection: {
        prefix: '.',
        className: 'text-size-8 text-green-700 uppercase font-bold',
        content: {
            fontSize: {sm: 'text-[0.9rem]', md: 'text-[0.95rem]'},
            lineHeight: {sm: 1.85, md: 1.65},
            spacing: 'space-y-4',
            align: 'center',
        },
    },
    chapter: {
        prefix: '.',
        className: 'text-size-7 text-primary font-bold',
        content: {
            fontSize: {sm: 'text-[0.9rem]', md: 'text-[0.95rem]'},
            lineHeight: {sm: 1.85, md: 1.65},
            spacing: 'space-y-4',
            align: 'left',
        },
    },
    section: {
        prefix: '.',
        className: 'text-size-6 text-green-700 uppercase font-bold',
        content: {
            fontSize: {sm: 'text-[0.9rem]', md: 'text-[0.95rem]'},
            lineHeight: {sm: 1.85, md: 1.65},
            spacing: 'space-y-3',
            align: 'left',
        },
    },
    subChapter: {
        prefix: '.',
        className: 'text-size-5 text-primary font-bold',
        content: {
            fontSize: {sm: 'text-[0.9rem]', md: 'text-[0.95rem]'},
            lineHeight: {sm: 1.85, md: 1.65},
            spacing: 'space-y-3',
            align: 'left',
        },
    },
    subSection: {
        prefix: '.',
        className: 'text-size-4 text-green-700 uppercase font-bold',
        content: {
            fontSize: {sm: 'text-[0.9rem]', md: 'text-[0.95rem]'},
            lineHeight: {sm: 1.85, md: 1.65},
            spacing: 'space-y-3',
            align: 'left',
        },
    },
    article: {
        prefix: '.',
        className: 'text-size-3 text-color-000 font-semibold',
        content: {
            fontSize: {sm: 'text-[0.9rem]', md: 'text-[0.95rem]'},
            lineHeight: {sm: 1.9, md: 1.7},
            spacing: 'space-y-2',
            align: 'justify',
        },
    },
    clause: {
        prefix: '.',
        className: 'text-size-2 text-color-100 font-medium',
        content: {
            fontSize: {sm: 'text-[0.9rem]', md: 'text-[0.95rem]'},
            lineHeight: {sm: 1.9, md: 1.7},
            spacing: 'space-y-2',
            align: 'justify',
        },
    },
    point: {
        prefix: '.',
        className: 'text-size-2 text-color-200 italic font-medium',
        content: {
            fontSize: {sm: 'text-[0.9rem]', md: 'text-[0.95rem]'},
            lineHeight: {sm: 1.9, md: 1.7},
            spacing: 'space-y-2',
            align: 'justify',
        },
    },
}

// --- inline styles map ---
const inlineStyles = {
    light: {key: 'light', style: {fontWeight: 300}},
    normal: {key: 'normal', style: {fontWeight: 'normal'}},
    medium: {key: 'medium', style: {fontWeight: 500}},
    bold: {key: 'bold', style: {fontWeight: 'bold'}},
    italic: {key: 'italic', style: {fontStyle: 'italic'}},
    underline: {key: 'underline', style: {textDecoration: 'underline'}},
    'line-through': {key: 'line-through', style: {textDecoration: 'line-through'}},
    right: {key: 'right', style: {textAlign: 'right', display: 'block'}},
    center: {key: 'center', style: {textAlign: 'center', display: 'block'}},
    'color-red': {key: 'color-red', style: {color: 'red'}},
    'color-blue': {key: 'color-blue', style: {color: 'blue'}},
    'color-yellow': {key: 'color-yellow', style: {color: 'yellow'}},
    'leading-none': {key: 'leading-none', style: {lineHeight: 1}},
    'leading-tight': {key: 'leading-tight', style: {lineHeight: 1.25}},
    'leading-snug': {key: 'leading-snug', style: {lineHeight: 1.375}},
    'leading-relaxed': {key: 'leading-relaxed', style: {lineHeight: 1.625}},
    'leading-loose': {key: 'leading-loose', style: {lineHeight: 2}},
    shadow: {
        key: 'shadow',
        style: {boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)'},
    },
    'shadow-md': {
        key: 'shadow-md',
        style: {boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)'},
    },
    'size-xs': {key: 'size-xs', style: {fontSize: '0.725em'}},
    'size-sm': {key: 'size-sm', style: {fontSize: '0.875em'}},
    'size-lg': {key: 'size-lg', style: {fontSize: '1.125em'}},
    'size-xl': {key: 'size-xl', style: {fontSize: '1.25em'}},
}

const escapeRegExp = (str = '') => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const withStyles = (item) => {
    const rowStyles = []
    const styles = String(item?.styles ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)

    styles.forEach((style) => {
        const currentStyle = _.get(inlineStyles, style)?.style
        if (currentStyle) rowStyles.push(currentStyle)
    })

    if (!rowStyles?.length) return {}

    return rowStyles.reduce((prev, current) => ({...prev, ...(current ?? {})}), {})
}

const styleCache = new Map()

const withStylesCached = (item) => {
    const key = item?.styles ?? ''
    if (styleCache.has(key)) return styleCache.get(key)

    const result = withStyles(item)
    styleCache.set(key, result)
    return result
}

function shallowEqual(objA, objB) {
    if (objA === objB) return true
    if (!objA || !objB) return false
    const keysA = Object.keys(objA)
    const keysB = Object.keys(objB)
    if (keysA?.length !== keysB?.length) return false
    for (const key of keysA) if (objA[key] !== objB[key]) return false
    return true
}

const HTMLRendererComponent = ({html, style, children, className, fallback}) =>
    html ? (
        <div
            style={style}
            className={classNames('HTMLRenderer text-center', className)}
            dangerouslySetInnerHTML={{__html: html}}
        >
            {children}
        </div>
    ) : (
        fallback ?? null
    )

export const HTMLRenderer = React.memo(HTMLRendererComponent, (prev, next) =>
    prev.html === next.html &&
    prev.className === next.className &&
    prev.fallback === next.fallback &&
    prev.children === next.children &&
    shallowEqual(prev.style, next.style)
)

const renderEnhancedContent = (item, params) => (fallback) => {
    const {content, style, is_html, className} = item ?? {}

    return is_html ? (
        <ContentSelector {...{item, params}}>
            <HTMLRenderer {...{style, className}} html={content}/>
        </ContentSelector>
    ) : (
        fallback ?? null
    )
}

const EmbedRowRender = React.memo(({item, style = {}}) => {
    const hasChildren = _.get(item, 'children', [])?.length > 0

    const mainContent = useMemo(() => {
        if (!item) return null

        const baseStyle = {...style, ...getStylesByKeys(_.get(item, 'styles', ''))}

        if (item?.short_index_name) {
            return (
                <div className="items-center space-x-1 lg:text-doc-2xs">
                    <GiIronCross size={10} className="text-primary"/>
                    {renderIf(item?.index_cd, <span className="text-primary-100">{item?.short_index_name}</span>)}
                    <span style={baseStyle}>{item.content}</span>
                </div>
            )
        }

        return (
            <div className="items-center space-x-1 text-lg md:text-base">
                {renderIf(item?.index_cd, <span className="font-bolder">{item?.index_cd}</span>)}
                <span style={baseStyle}>{item.content}</span>
            </div>
        )
    }, [item?.id])

    const childrenContent = useMemo(() => {
        if (!hasChildren) return null
        return (
            <div className="flex flex-col space-y-3 ml-3">
                {item.children.map((child, i) => (
                    <React.Fragment key={child?.id}>
                        <EmbedRowRender item={child} style={{paddingLeft: '0.5rem'}}/>
                    </React.Fragment>
                ))}
            </div>
        )
    }, [hasChildren, item.children])

    return (
        <div className="flex flex-col gap-1 background rounded-lg">
            <div className="flex items-center flex-wrap gap-2 font-medium italic">{mainContent}</div>
            {childrenContent}
        </div>
    )
})

const ContentSelector = ({item, params, style, className, children}) => {
    const t = useTranslate()
    const {onItemClick} = params ?? {}
    const {system_row} = useContext(CodiContext)

    const selected = system_row?.id === item?.id

    const haveEmbedRows =
        Array.isArray(_.get(item, 'embed_rows')) &&
        _.get(item, 'embed_rows', [])?.length > 0

    const handleClick = (event, itemNode, onItemClickFn = NullAsDefault) => {
        event.stopPropagation()
        onItemClickFn(itemNode)
    }

    const renderEmbedContentSelector = (
        childrenNode,
        itemNode,
        selected,
        onItemClickFn
    ) => (
        <div
            onClick={(e) => handleClick(e, itemNode, onItemClickFn)}
            style={{
                padding: selected ? '0.5rem' : undefined,
                backgroundColor: 'transparent',
            }}
            className={classNames(
                'hover-back cursor-pointer',
                selected && 'rounded-xs marker-highlight'
            )}
        >
            <div style={{marginLeft: selected ? '-0.75rem' : undefined, display: 'unset'}}>
                {childrenNode}
                {!selected && (
                    <span className="text-sm italic cursor-pointer text-primary-500">
            ({t('tap to view')})
          </span>
                )}
            </div>

            {selected && (
                <div className="background rounded-lg p-2 mt-1">
                    {_.get(itemNode, 'embed_rows', []).map((e, i) => (
                        <React.Fragment key={e?.id}>
                            <EmbedRowRender
                                item={e}
                                style={{fontSize: '0.85rem', marginLeft: 5}}
                            />
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    )

    const renderContentSelector = (
        childrenNode,
        itemNode,
        selected,
        onItemClickFn
    ) => (
        <div
            onClick={(e) => handleClick(e, itemNode, onItemClickFn)}
            style={{backgroundColor: 'transparent'}}
            className={classNames(
                'hover-back cursor-pointer',
                selected && 'rounded marker-highlight'
            )}
        >
            {childrenNode}
        </div>
    )

    if (!_.isFunction(onItemClick)) return children

    return (
        <div
            tabIndex={0}
            style={{...(style ?? {}), backgroundColor: 'transparent'}}
            className={classNames('hover-container', className)}
        >
            {haveEmbedRows
                ? renderEmbedContentSelector(children, item, selected, onItemClick)
                : renderContentSelector(children, item, selected, onItemClick)}
        </div>
    )
}


const CodiTitle = React.memo(({item, style, className, children, prefix = ''}) => {
    const {id, index_cd, content} = item ?? {}
    const title = `${index_cd ?? ''}${index_cd && content ? prefix : ''}`

    return (
        <div
            style={{...(style ?? {}), ...withStylesCached(item)}}
            id={content ? undefined : id}
            className={classNames('flex-1 space-x-1 leading-snug', className)}
        >
            {(index_cd || content) && <span>{title}</span>}
            {children}
        </div>
    )
})


const NestedContent = ({item, params, style, className}) => {
    const children = item?.children_rows ?? []

    if (!children?.length) return null

    return (
        <div
            style={style}
            className={classNames('NestedContent space-y-3', className)}
        >
            {children.map((row, index) => (
                <Render key={row?.id ?? index} item={row} params={params}/>
            ))}
        </div>
    )
}


const Sentence = React.memo(({item, style, className, params}) => {

    const {sentence_cd, index_cd} = item
    const {onItemClick = NullAsDefault, index = 0} = params ?? {}

    const default_number = Number(index) + 1
    const numberLabel = sentence_cd ?? index_cd ?? default_number

    const {system_row} = useContext(CodiContext)
    const selected = system_row?.id === item?.id
    if (!item) return null

    return (
        <span
            style={style}
            onClick={() => onItemClick(item)}
            className={classNames(
                'RowSentence whitespace-pre-line inline',
                _.isFunction(onItemClick) && 'cursor-pointer',
                className
            )}
        >
      <sup className="font-bold text-xs text-red-500 ml-2 mr-1 italic">
        {numberLabel}
      </sup>

      <Content
          item={item}
          params={params}
          style={{
              letterSpacing: '0.4px',
              wordSpacing: '0.5px',
              ...withStylesCached(item),
          }}
          className={classNames(
              'inline text-color',
              selected && 'rounded marker-highlight'
          )}
      />
    </span>
    )
})

const Paragraph = React.memo(({item, params}) => {
    const t = useTranslate()

    const children = item?.children_rows ?? []

    if (_.isEmpty(children)) {
        const indexCd = _.get(item, 'index_cd')

        return (
            <ContentSelector item={item} params={params}>
                {indexCd && (
                    <span className="font-bold text-color-000 italic">
            {indexCd}
          </span>
                )}
                <Content
                    item={item}
                    params={params}
                    style={{
                        letterSpacing: '0.4px',
                        wordSpacing: '0.5px',
                        ...withStylesCached(item),
                    }}
                    className="mx-5 text-center text-color"
                />
            </ContentSelector>
        )
    }

    return (
        <div
            style={{display: 'block', lineHeight: '2'}}
            className="RowParagraph flex relative space-y-2"
        >
            {children.map((sentence, index) => (
                <Sentence
                    key={sentence?.id ?? index}
                    item={sentence}
                    params={{...(params ?? {}), index}}
                    style={{display: 'inline', ...withStylesCached(sentence)}}
                />
            ))}
        </div>
    )
})

const mapContentRefs = (content, refs) => {
    let result = content

    const links = [...(refs?.links ?? [])].sort(
        (a, b) => (b?.key?.length || 0) - (a?.key?.length || 0)
    )

    const notes = [...(refs?.notes ?? [])].sort(
        (a, b) => (b?.key?.length || 0) - (a?.key?.length || 0)
    )

    const linkMap = new Map(
        links.filter(l => l?.key).map(l => [l.key, l])
    )

    const noteMap = new Map(
        notes.filter(n => n?.key).map(n => [n.key, n])
    )

    // --- links ---
    for (const [key, link] of linkMap) {
        const escapedKey = escapeRegExp(key)
        const regex = new RegExp(`(${escapedKey})`, 'gi')

        result = nodeReplace(result, regex, (match, index) => (
            <Link
                key={match + index}
                target="_blank"
                to={getLinkToDetailRow(link.link)}
                onClick={(e) => e.stopPropagation()}
            >
                <span className="font-medium text-primary italic">{match}</span>
            </Link>
        ))
    }

    // --- notes ---
    for (const [key, note] of noteMap) {
        const escapedKey = escapeRegExp(key)
        const regex = new RegExp(`(${escapedKey})`, 'gi')

        result = nodeReplace(result, regex, (match, index) => (
            <TooltipApp key={match + index} title={note.text_content}>
        <span className="font-bold text-sm text-green-700 italic mx-1">
          {match}
        </span>
            </TooltipApp>
        ))
    }

    return Array.isArray(result)
        ? result.map((c, i) => <React.Fragment key={i}>{c}</React.Fragment>)
        : result ?? content
}


const Content = ({item, style, params, className}) => {
    const {isSm} = useContext(LayoutContext)
    const {onItemClick = NullAsDefault} = params ?? {}
    const {system_row} = useContext(CodiContext)

    const selected =
        system_row?.id === item?.id ||
        system_row?.compare_id === item?.compare_id

    if (!item?.content) return null

    const renderedContent = mapContentRefs(item.content, item)

    return (
        <div
            id={item?.id}
            style={style}
            onClick={(event) => {
                event.stopPropagation()
                onItemClick(item)
            }}
            className={classNames(
                'RowContent relative gap-2 inline',
                isSm ? 'anchor-double-offset' : 'anchor-offset',
                _.isFunction(onItemClick) && 'cursor-pointer',
                selected && 'rounded marker-highlight font-medium',
                className
            )}>
            {renderedContent}
        </div>
    )
}


const CodiBlock = React.memo(({item, params, Wrapper = 'div', levelClass = ''}) => {
    const {isSm} = useContext(LayoutContext)

    const type = item?.index_typecd?.toLowerCase?.()
    const config = configs[type] ?? {}

    const contentCfg = config.content ?? {
        fontSize: {sm: 'text-sm', md: 'text-base'},
        lineHeight: {sm: 1.9, md: 1.6},
        spacing: 'space-y-2',
        align: 'left',
    }

    const fontSizeClass = isSm
        ? contentCfg.fontSize.sm
        : contentCfg.fontSize.md

    const alignClass =
        contentCfg.align === 'justify'
            ? 'text-justify'
            : contentCfg.align === 'center'
            ? 'text-center'
            : 'text-left'

    const contentClassName = classNames(
        fontSizeClass,
        alignClass,
        'whitespace-pre-line break-words text-color leading-relaxed readable'
    )

    const contentStyle = {
        letterSpacing: '0.4px',
        wordSpacing: '0.5px',
        lineHeight: isSm
            ? contentCfg.lineHeight.sm
            : contentCfg.lineHeight.md,
        ...withStylesCached(item),
    }

    return (
        <div
            id={item?.id}
            className={classNames(
                'CodiBlock flex flex-col relative',
                levelClass,
                contentCfg.spacing
            )}
        >
            <Wrapper
                className={classNames(
                    'flex items-center gap-2 py-2',
                    contentCfg.align === 'center'
                        ? 'justify-center'
                        : 'justify-start'
                )}
            >
                <ContentSelector item={item} params={params}>
                    <CodiTitle
                        item={item}
                        prefix={config?.prefix}
                        className={config?.className}
                    >
                        <Content
                            item={item}
                            params={params}
                            className={contentClassName}
                            style={contentStyle}
                        />
                    </CodiTitle>
                </ContentSelector>
            </Wrapper>

            <NestedContent item={item} params={params}/>
        </div>
    )
})


// --- specific wrappers (lightweight) ---
const SuperChapter = (props) => <CodiBlock {...props} levelClass="RowSuperChapter"/>
const SubChapter = ({item, params, Wrapper = 'div'}) => (
    <div className="RowSubChapter flex flex-col relative">
        <Wrapper className="flex items-center gap-2 py-2">
            <ContentSelector {...{item, params}}>
                <CodiTitle item={item} prefix={configs.subChapter.prefix} className={configs.subChapter.className}>
                    <Content item={item} params={params}/>
                </CodiTitle>
            </ContentSelector>
        </Wrapper>
        <NestedContent item={item} params={params}/>
    </div>
)

const Chapter = (props) => <CodiBlock {...props} levelClass="RowChapter"/>

const SuperSection = ({item, params, Wrapper = 'div'}) => (
    <div className="RowSuperSection flex flex-col relative">
        <Wrapper className="flex items-center gap-2 py-2">
            <ContentSelector {...{item, params}}>
                <CodiTitle item={item} prefix={configs.superSection.prefix} className={configs.superSection.className}>
                    <Content item={item} params={params}/>
                </CodiTitle>
            </ContentSelector>
        </Wrapper>
        <NestedContent item={item} params={params}/>
    </div>
)

const SubSection = ({item, params, Wrapper = 'div'}) => (
    <div className="RowSubSection flex flex-col relative">
        <Wrapper className="flex items-center gap-2 py-2">
            <ContentSelector {...{item, params}}>
                <CodiTitle item={item} prefix={configs.subSection.prefix} className={configs.subSection.className}>
                    <Content item={item} params={params}/>
                </CodiTitle>
            </ContentSelector>
        </Wrapper>
        <NestedContent item={item} params={params}/>
    </div>
)

const Section = (props) => <CodiBlock {...props} levelClass="RowSection"/>
const SuperPart = (props) => <CodiBlock {...props} levelClass="RowSuperPart"/>
const Part = (props) => <CodiBlock {...props} levelClass="RowPart"/>
const Article = (props) => <CodiBlock {...props} levelClass="RowArticle"/>
const Appendix = (props) => <CodiBlock {...props} levelClass="RowAppendix"/>
const Clause = (props) => <CodiBlock {...props} levelClass="RowClause"/>
const Point = (props) => <CodiBlock {...props} levelClass="RowPoint"/>

export const Root = (props) => <CodiBlock {...props} levelClass="RowRoot"/>

const COMPONENT_MAP = {
    [CodiContentTypes.ROOT]: Root,
    [CodiContentTypes.CHAPTER]: Chapter,
    [CodiContentTypes.SUB_CHAPTER]: SubChapter,
    [CodiContentTypes.SUPER_CHAPTER]: SuperChapter,
    [CodiContentTypes.SECTION]: Section,
    [CodiContentTypes.SUB_SECTION]: SubSection,
    [CodiContentTypes.SUPER_SECTION]: SuperSection,
    [CodiContentTypes.APPENDIX]: Appendix,
    [CodiContentTypes.SUPER_PART]: SuperPart,
    [CodiContentTypes.PART]: Part,
    [CodiContentTypes.ARTICLE]: Article,
    [CodiContentTypes.CLAUSE]: Clause,
    [CodiContentTypes.POINT]: Point,
    [CodiContentTypes.SENTENCE]: Sentence,
    [CodiContentTypes.PARAGRAPH]: Paragraph,
    [CodiContentTypes.CONTENT]: Content,
}

const HEADING_TAGS = ['div', 'h2', 'h3', 'h4', 'h5', 'h6', 'h7']


const Render = React.memo(({item, params, children}) => {
    useTranslate()

    const Component =
        COMPONENT_MAP[item?.index_typecd] ??
        (() => children ?? null)

    const depth = _.get(item, 'depth_level', 0)
    const Wrapper = HEADING_TAGS[depth] ?? 'div'

    const enhance = renderEnhancedContent(item, params)

    return enhance(
        <Component item={item} params={params} Wrapper={Wrapper}/>
    )
})

export default {
    Root,
    Render,
    Point,
    Part,
    Clause,
    Section,
    Chapter,
    Content,
    Article,
    Appendix,
    Sentence,
    Paragraph,
    HTMLRenderer,
}
